import {
  NameIdTuple,
  RecycleBinViewDTO,
  RestoreResponseDTO,
} from 'business/models/Models';
import RecycleBinService from 'business/services/RecycleBinService';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import RMTable from 'components/customComponents/Controls/RMTable';
import { LoadingModal, RMModalProps, useModalHandlers } from 'components/modal';
import React, { useCallback, useEffect, useState } from 'react';
import { options } from '../main/tree/utils';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { getTreeNodeImageProps, setSystemStateBusy } from 'utils/Global';
import { RecycleBinTableView } from './constants';
import { RestoreResponseModalBody } from './modals/RestoreResponseModalBody';
import { TreeNodeTypeNames } from 'enums/ETreeNodeTypes';
import useRMLite from 'CustomHooks/useRMLite';
import { useNavigate } from 'react-router';
import { useEventListener } from 'CustomHooks/useEventListener';
import { KeyboardKeys } from 'constants/';
import { getNormalizedKeyboardKey } from 'keyboardShortcuts/utils';
import { FmlxButton } from 'fmlx-common-ui';
import { useSystemState } from 'redux/hooks/common';
import { SystemState } from 'redux/reducers/commonReducer';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';

type Props = {
  onRefreshTreeNode: (node) => void;
};
export const RecycleBin = (props: Props) => {
  const { onRefreshTreeNode } = props;
  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState<RecycleBinTableView[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const groupingColumns = ['type'];
  const recycleBinService = new RecycleBinService();
  const { showModal, hideModal } = useModalHandlers();
  const navigate = useNavigate();
  const rmLite = useRMLite();
  const systemState = useSystemState();
  const isSystemBusy = systemState !== SystemState.IDLE;
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);

  const handleSelectionChange = useCallback((rowIndices) => {
    setSelectedRows([...rowIndices]);
  }, []);

  const HandleRestore = async (dependantNodes: NameIdTuple[]) => {
    hideModal();
    setIsLoading(true);
    const ids = [
      ...new Set([
        ...selectedRows.map((index) => data[index].treeNodeId),
        ...dependantNodes.map((n) => n.id),
      ]),
    ];
    const isBusy = await setSystemStateBusy();
    let recycleBinData: RestoreResponseDTO;
    try {
      recycleBinData = await recycleBinService.Restore(ids);
    } finally {
      isBusy.dispose();
    }
    if (recycleBinData.errors.length || recycleBinData.warnings.length) {
      showModal({
        heading: 'Restore File',
        body: <RestoreResponseModalBody response={recycleBinData} />,
        onCancel: hideModal,
        isCancelOnly: true,
        noButtonText: 'OK',
        dialogClassName: 'max-w-[40rem] w-[40rem]',
      });
    }
    // Select distinct parent ids from restored nodes and refresh all parent nodes
    if (recycleBinData.restoredParentIds?.length) {
      recycleBinData.restoredParentIds.forEach((parentId) =>
        onRefreshTreeNode({ id: parentId }),
      );
      explorerBroadcastChannel?.postMessage({
        action: CHANNEL_ACTIONS.EXPLORER_RESTORE_NODE,
        payload: { parentIds: recycleBinData.restoredParentIds },
      });
    }
    await fetchRecycleBin();
    setSelectedRows([]);
    setIsLoading(false);
  };

  const showRestoreModal = (depNodes: NameIdTuple[]) => {
    const modalProps = getRestoreWarningModal(
      selectedRows,
      depNodes,
      HandleRestore,
      hideModal,
    );
    showModal(modalProps);
  };

  const showDeleteModal = (depNodes: NameIdTuple[]) => {
    const filteredDeletedNodes = depNodes.filter((dep) =>
      selectedRows.every((index) => data[index].treeNodeId !== dep.id),
    );
    const modalProps = getDeleteWarningModal(
      filteredDeletedNodes,
      HandleDelete,
      hideModal,
    );
    showModal(modalProps);
  };

  const HandleDelete = async (dependantNodes: NameIdTuple[]) => {
    hideModal();
    setIsLoading(true);
    const ids = [
      ...new Set([
        ...selectedRows.map((index) => data[index].treeNodeId),
        ...dependantNodes.map((n) => n.id),
      ]),
    ];
    const recycleBinData = await recycleBinService.DeleteNow(ids);
    if (!recycleBinData) {
      return null;
    }
    await fetchRecycleBin();
    setSelectedRows([]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (rmLite) {
      navigate('/');
    } else {
      fetchRecycleBin();
    }
  }, []);

  const fetchRecycleBin = async () => {
    const recycleBinData = await recycleBinService.GetRecycleBinView();
    if (!recycleBinData) {
      return null;
    }
    const tableData = recycleBinData.map((item: RecycleBinViewDTO) => {
      return {
        ...item,
        name: <TrashNode item={item} />,
        type: TreeNodeTypeNames[item.type],
      };
    });
    setData(tableData);
  };

  const checkDeleteDependencies = async () => {
    const ids = selectedRows.map((index) => data[index].treeNodeId);
    const depNodes = await recycleBinService.CheckDeleteDependencies(ids);
    showDeleteModal(depNodes);
  };

  const checkRestoreDependencies = async () => {
    const ids = selectedRows.map((index) => data[index].treeNodeId);
    const depNodes = await recycleBinService.CheckRestoreDependencies(ids);
    showRestoreModal(depNodes);
  };

  useEventListener('keydown', (e) => {
    const key = getNormalizedKeyboardKey(e);
    if (e.ctrlKey && key === KeyboardKeys.A) {
      e.preventDefault();
      setSelectedRows(data.map((_, index) => index));
    }
  });
  return (
    <div id="recycle-bin-wrapper" className="w-full h-full">
      <div id="recycle-bin-button" className="flex flex-row">
        <RMCustomButton
          title="RESTORE"
          contentContainerClass="tree-control-open-button m-2"
          titleClass="tree-control-open-button-title"
          onClick={checkRestoreDependencies}
          isDisabled={!selectedRows.length || isSystemBusy}
          titleTip={
            isSystemBusy
              ? 'Disabled because the system is currently busy. Please wait...'
              : ''
          }
        />
        <RMCustomButton
          title="DELETE"
          contentContainerClass="tree-control-open-button m-2"
          titleClass="tree-control-open-button-title"
          onClick={checkDeleteDependencies}
          isDisabled={!selectedRows.length}
        />
      </div>
      <div id="recycle-bin-table" className="w-full h-full">
        <RMTable
          columns={options}
          data={data}
          selectedIds={selectedRows}
          onCheckboxSelectionChange={handleSelectionChange}
          groupingColumns={groupingColumns}
          hiddenColumns={groupingColumns}
          tableClass="w-full"
          selectRowOnClick
        ></RMTable>
      </div>
      <LoadingModal showModal={isLoading} text={'Loading'} />
    </div>
  );
};

const TrashNode = ({ item }) => {
  const iconProps = getTreeNodeImageProps({ ...item, allowEdits: true });
  return (
    <div id={`recycle-bin-trash-node-${item.id}`} className="flex flex-row gap-4">
      <RMSvgIcon id={`recycle-bin-trash-node-icon-${item.id}`} {...iconProps} />
      <div id={`recycle-bin-trash-node-label-${item.id}`} className="flex items-center">
        {item.name}
      </div>
    </div>
  );
};

const getDeleteWarningModal = (dependantNodes, deleteHandler, cancalHandler) => {
  const DelateWarning = {
    type: 'warning',
    message:
      "This action will permanently delete items and you can't restore them later. The deletion process operates in the background and may affect ROCK MAKER performance.",
  };
  const deleteError = {
    type: 'error',
    message: (
      <div
        id="recycle-bin-delete-error-wrapper"
        className="w-[37rem] max-h-28 overflow-y-auto"
      >
        {dependantNodes.map((n) => (
          <>
            <span id={`recycle-bin-delete-error-nodes-${n.name}`}>{`• ${n.name}`}</span>
            <br />
          </>
        ))}
      </div>
    ),
  };
  return {
    inlineMessage: dependantNodes.length ? deleteError : DelateWarning,
    heading: dependantNodes.length
      ? 'Unable to Permanent Delete'
      : 'Permanently Delete Items',
    okButtonText: dependantNodes.length ? 'Force Delete' : 'Delete',
    body: (
      <div id="recycle-bin-delete-warning-modal-body" className="text-[#5D5D5D]">
        {dependantNodes.length
          ? 'Some of the items you have selected can not be deleted because they are dependent on other items in the recycle bin. Would you like to permanently delete all items'
          : 'Permanently delete these items?'}
      </div>
    ),
    customFooter: (
      <div
        id="recycle-bin-delete-warning-footer"
        className="flex flex-row items-center justify-end w-full h-12 gap-3 px-2 mt-4 border-t border-t-[#CACACA] border-solid border-0"
      >
        <FmlxButton
          id="recycle-bin-delete-warning-footer-delete-button"
          onClick={() => {
            deleteHandler(dependantNodes);
          }}
          type="primary"
          variant="outline"
          label={dependantNodes.length ? 'Force Delete' : 'Delete'}
        />
        <FmlxButton
          id="recycle-bin-delete-warning-footer-cancel-button"
          onClick={cancalHandler}
          label="Cancel"
        />
      </div>
    ),
  } as RMModalProps;
};

const getRestoreWarningModal = (
  selectedRows,
  dependantNodes,
  restoreHandler,
  cancalHandler,
) => {
  return {
    inlineMessage: dependantNodes.length
      ? {
          type: 'warning',
          message: (
            <>
              <div
                id="recycle-bin-restore-warning-modal-inlinemessage"
                className="break-words"
              >
                Note: Restoring deleted experiments will also restore any associated
                layers that were previously deleted.
              </div>
              <div
                id="recycle-bin-restore-warning-modal-inlinemessage-nodes"
                className=" max-h-24 overflow-y-auto ml-2"
              >
                {dependantNodes.map((n) => (
                  <>
                    <span
                      id={`recycle-bin-restore-warning-modal-inlinemessage-nodes-${n.name}`}
                    >{`• ${n.name}`}</span>
                    <br />
                  </>
                ))}
              </div>
            </>
          ),
        }
      : {},
    heading: 'Restore File',
    okButtonText: 'Restore All',
    body: (
      <div id="recycle-bin-restore-warning-modal-body" className="text-[#5D5D5D]">
        {dependantNodes.length
          ? `Do you want to restore [${selectedRows.length}] files
        along with its associated data?`
          : `Restore [${selectedRows.length}] items?`}
      </div>
    ),
    customFooter: (
      <div
        id="recycle-bin-restore-warning-modal-footer-wrapper"
        className="flex flex-row items-center justify-end w-full h-12 gap-3 px-2 mt-4 border-t border-t-[#CACACA] border-solid border-0"
      >
        <FmlxButton
          id="recycle-bin-restore-warning-modal-footer-cancel-button"
          type="primary"
          variant="outline"
          onClick={cancalHandler}
          label="Cancel"
        />
        <FmlxButton
          id="recycle-bin-restore-warning-modal-footer-restoreall-button"
          label="Restore All"
          onClick={() => {
            restoreHandler(dependantNodes);
          }}
        />
      </div>
    ),
  } as RMModalProps;
};
